import React, { forwardRef, useLayoutEffect, useRef, useImperativeHandle } from "react";
import { cn } from "./utils";

interface NgocInputProps {
    placeholder?: string;
    maxLength?: number;
    rows?: number;
    className?: string;
    value?: string | number | readonly string[] | undefined;
}

export const  NgocInput = forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(({ placeholder, maxLength, rows = 1, value, className, ...props }: NgocInputProps, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Expose the inner textarea to parent refs
  useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

  // Auto-resize on mount and whenever value changes
  useLayoutEffect(() => {
    autoResize(textareaRef.current);
  }, [value]);

  const handleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const textarea = event.target as HTMLTextAreaElement;
    autoResize(textarea);
  };

  return (
    <textarea
      rows={rows}
      ref={textareaRef}
      value={value}
      onInput={handleInput}
      {...props}
      style={{
        width: "100%",
        resize: "none",
        overflow: "hidden",
      }}
      maxLength={maxLength}
      placeholder={placeholder}
      className={cn(
        "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex w-full min-w-0 rounded-md border px-3 pt-[7px] text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        className
      )}
    />
  );
})